import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components/link/src/link.tsx";
import {
    Lozenge,
    LozengeAppearance
} from "@/libraries/design/components/lozenge/src/lozenge.tsx";
import { wacToken } from "@/libraries/design/foundations/index.ts";

export interface CardProps {
    onClick: () => void;
    data: FeatureData;
}

export type FeatureData = {
    featureName: string;
    category: string;
    status: Status;
    timeFrame: string;
    apps: string[];
    slug: string;
    description?: string;
};

export type Status = "future" | "coming soon" | "released" | "completed";

export const Card = (props: CardProps) => {
    const { data } = props;

    const {
        featureName,
        description,
        category,
        status,
        timeFrame,
        apps,
        slug
    } = data;

    const statusColor = getStatusColor(data.status);

    const intl = useIntl();
    const learnMoreLabel = intl.formatMessage({
        id: "roadmapFeatures.learnMore",
        defaultMessage: "Learn more"
    });

    return (
        <div css={cardRowCss}>
            <div css={cellCss}>
                <h3 css={featureNameCss}>{featureName}</h3>
                <p>{description}</p>
                <div>
                    <Link
                        text={learnMoreLabel}
                        aria-label={learnMoreLabel}
                        href={slug}
                    />
                </div>
            </div>
            <div css={cellCss}>{category}</div>
            <div css={[cellCss, lozengeCellCss]}>
                <Lozenge text={status} appearance={statusColor} />
            </div>
            <div css={cellCss}>{timeFrame}</div>
            <div css={[cellCss, lozengeCellCss]}>
                {apps.map((app) => (
                    <Lozenge key={app} text={app} appearance="neutral" />
                ))}
            </div>
        </div>
    );
};

const getStatusColor = (status: Status) => {
    let statusColor: LozengeAppearance = "neutral";
    if (status === "future") {
        statusColor = "purple";
    }
    if (status === "coming soon") {
        statusColor = "yellow";
    }
    if (status === "released") {
        statusColor = "lime";
    }
    if (status === "completed") {
        statusColor = "neutral";
    }
    return statusColor;
};

const cardRowCss = css({
    "width": "100%",
    "display": "grid",
    "gridTemplateColumns":
        "minmax(34%, 4fr) minmax(100px, 1fr) minmax(75px, 1fr) 1fr minmax(188px, 1fr)",
    "gridTemplateRows": "auto",
    "gap": `0px ${token("space.300")}`,
    "alignItems": "start",
    "justifyContent": "start",
    "padding": token("space.300"),
    "border": `1px solid ${wacToken("color.neutral.light400")}`,
    "backgroundColor": wacToken("color.neutral.light0"),
    "borderRadius": wacToken("corner.radius.card"),
    "cursor": "pointer",
    "boxSizing": "border-box",
    "transition": "box-shadow 150ms ease, transform 150ms ease",
    "&:hover": {
        transition: "box-shadow 125ms ease-out",
        boxShadow: token("elevation.shadow.overlay")
    },
    [media.below.md]: {
        display: "flex",
        flexDirection: "column",
        gap: token("space.150")
    }
});

const cellCss = css({
    "textAlign": "left",
    "@media (max-width: 900px)": {
        width: "100%"
    },
    "display": "flex",
    "flexWrap": "wrap",
    "gap": token("space.050")
});

const lozengeCellCss = css({
    marginBlockStart: "2px"
});

const featureNameCss = css({
    margin: 0,
    fontSize: "24px !important",
    lineHeight: "28px"
});
